import { CircleAlert, CircleCheck, Info, TriangleAlert, X, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useT } from '@/shared/i18n/useT';
import { cn } from '@/shared/utils/cn';
import { Icon } from '../icons/Icon';
import { IconButton } from './IconButton';

const TONES = {
  info: { icon: Info, box: 'border-info/30 bg-info-tint', accent: 'text-info' },
  warn: { icon: TriangleAlert, box: 'border-warn/30 bg-warn-tint', accent: 'text-warn' },
  error: { icon: CircleAlert, box: 'border-down/30 bg-down-tint', accent: 'text-down' },
  success: { icon: CircleCheck, box: 'border-up/30 bg-up-tint', accent: 'text-up' },
  neutral: { icon: Info, box: 'border-border bg-muted', accent: 'text-fg-muted' },
} as const satisfies Record<string, { icon: LucideIcon; box: string; accent: string }>;

export interface BannerProps {
  tone?: keyof typeof TONES;
  title?: string;
  children?: ReactNode;
  /** Acción puntual (enlace o botón pequeño). */
  action?: ReactNode;
  onDismiss?: () => void;
  icon?: ReactNode;
  className?: string;
}

/** Aviso en línea: icono + texto + acción. `error` se anuncia como alerta; el resto, como estado (polite). */
export function Banner({
  tone = 'info',
  title,
  children,
  action,
  onDismiss,
  icon,
  className,
}: BannerProps) {
  const { t } = useT();
  const spec = TONES[tone];
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={cn(
        'flex items-start gap-3 rounded-md border px-4 py-3 text-sm text-fg',
        spec.box,
        className,
      )}
    >
      <span className={cn('mt-0.5 shrink-0', spec.accent)}>
        {icon ?? <Icon icon={spec.icon} />}
      </span>
      <div className="min-w-0 flex-1">
        {title ? <p className={cn('font-semibold', spec.accent)}>{title}</p> : null}
        {children ? <div className={cn(title && 'mt-0.5')}>{children}</div> : null}
        {action ? <div className="mt-2">{action}</div> : null}
      </div>
      {onDismiss ? (
        <IconButton
          label={t('actions.close')}
          icon={<Icon icon={X} size={16} />}
          iconSize="sm"
          onClick={onDismiss}
          className="-my-1.5 -mr-2 shrink-0"
        />
      ) : null}
    </div>
  );
}
